import { lazy, Suspense } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import { WorkspaceLayout } from '@/app/layout/WorkspaceLayout';
import { withChunkRecovery } from '@/app/routes/chunk-error-recovery';
import { paths } from '@/app/routes/paths';
import { RequireAuth } from '@/app/routes/RequireAuth';
import { RequireOnboardingComplete } from '@/app/routes/RequireOnboardingComplete';
import { RequireParticipantsManage } from '@/app/routes/RequireParticipantsManage';
import { RequireVerified } from '@/app/routes/RequireVerified';
import { RedirectGuardianToDashboard } from '@/app/routes/RedirectGuardianToDashboard';
import { RequireGuardianOnly } from '@/app/routes/RequireGuardianOnly';
import { RequireOrganisationSettingsAccess } from '@/app/routes/RequireOrganisationSettingsAccess';
import { RootRedirect } from '@/app/routes/RootRedirect';
import { OnboardingIndexRedirect } from '@/features/onboarding/components/OnboardingIndexRedirect';
import { OnboardingLayout } from '@/features/onboarding/components/OnboardingLayout';
import { RouteLoadingFallback } from '@/shared/components/RouteLoadingFallback';

/**
 * Tiny helper that maps a named export into the default shape `React.lazy`
 * expects, and — importantly — runs the `import()` through
 * {@link withChunkRecovery} so a stale-hash chunk after a deploy triggers a
 * single page reload instead of an "Unexpected Application Error" screen.
 */
function lazyNamed<K extends string, P>(
  loader: () => Promise<Record<K, ComponentType<P>>>,
  name: K,
): ComponentType<P> {
  const safeLoader = withChunkRecovery(loader);
  // `React.lazy` returns a `LazyExoticComponent<T>` whose callable shape is
  // equivalent to `ComponentType<T>` at runtime; the cast keeps call-sites
  // from needing to know about `LazyExoticComponent`.
  return lazy(async () => {
    const mod = await safeLoader();
    return { default: mod[name] };
  }) as unknown as ComponentType<P>;
}

const LoginPage = lazyNamed(() => import('@/features/auth/pages/LoginPage'), 'LoginPage');
const SignUpPage = lazyNamed(() => import('@/features/auth/pages/SignUpPage'), 'SignUpPage');
const VerifyEmailPage = lazyNamed(
  () => import('@/features/auth/pages/VerifyEmailPage'),
  'VerifyEmailPage',
);
const PortalInviteActivatePage = lazyNamed(
  () => import('@/features/auth/pages/PortalInviteActivatePage'),
  'PortalInviteActivatePage',
);
const DashboardGatePage = lazyNamed(
  () => import('@/features/dashboard/pages/DashboardGatePage'),
  'DashboardGatePage',
);
const HubPlaceholderPage = lazyNamed<
  'HubPlaceholderPage',
  { hubKey: 'rosterLogistics' }
>(() => import('@/features/dashboard/pages/HubPlaceholderPage'), 'HubPlaceholderPage');
const TrainingHubPage = lazyNamed(
  () => import('@/features/training/pages/TrainingHubPage'),
  'TrainingHubPage',
);
const TrainingNewProgramPage = lazyNamed(
  () => import('@/features/training/pages/TrainingNewProgramPage'),
  'TrainingNewProgramPage',
);
const TrainingProgramPreviewPage = lazyNamed(
  () => import('@/features/training/pages/TrainingProgramPreviewPage'),
  'TrainingProgramPreviewPage',
);
const TrainingProgramDetailPage = lazyNamed(
  () => import('@/features/training/pages/TrainingProgramDetailPage'),
  'TrainingProgramDetailPage',
);
const WorkforceManagementPage = lazyNamed(
  () => import('@/features/workforce/pages/WorkforceManagementPage'),
  'WorkforceManagementPage',
);
const WorkforceNewStaffPage = lazyNamed(
  () => import('@/features/workforce/pages/WorkforceNewStaffPage'),
  'WorkforceNewStaffPage',
);
const WorkforceNewCareTeamPage = lazyNamed(
  () => import('@/features/workforce/pages/WorkforceNewCareTeamPage'),
  'WorkforceNewCareTeamPage',
);
const WorkforceStaffProfilePage = lazyNamed(
  () => import('@/features/workforce/pages/WorkforceStaffProfilePage'),
  'WorkforceStaffProfilePage',
);
const PatientsHubPage = lazyNamed(() => import('@/features/patients/pages/PatientsHubPage'), 'PatientsHubPage');
const GuardianDashboardPage = lazyNamed(
  () => import('@/features/guardian/pages/GuardianDashboardPage'),
  'GuardianDashboardPage',
);
const GuardianParticipantProfilePage = lazyNamed(
  () => import('@/features/guardian/pages/GuardianParticipantProfilePage'),
  'GuardianParticipantProfilePage',
);
const ArchivedPatientsPage = lazyNamed(
  () => import('@/features/patients/pages/ArchivedPatientsPage'),
  'ArchivedPatientsPage',
);
const PatientsImportReviewPage = lazyNamed(
  () => import('@/features/patients/pages/PatientsImportReviewPage'),
  'PatientsImportReviewPage',
);
const NewPatientIntakePage = lazyNamed(
  () => import('@/features/patients/pages/NewPatientIntakePage'),
  'NewPatientIntakePage',
);
const PatientProfilePage = lazyNamed(
  () => import('@/features/patients/pages/PatientProfilePage'),
  'PatientProfilePage',
);
const PatientCarePlanDraftPage = lazyNamed(
  () => import('@/features/patients/pages/PatientCarePlanDraftPage'),
  'PatientCarePlanDraftPage',
);
const OrganisationIdentityPage = lazyNamed(
  () => import('@/features/onboarding/pages/OrganisationIdentityPage'),
  'OrganisationIdentityPage',
);
const ServiceSitesPage = lazyNamed(
  () => import('@/features/onboarding/pages/ServiceSitesPage'),
  'ServiceSitesPage',
);
const OrganisationSettingsModule = lazyNamed(
  () => import('@/features/organisation/OrganisationSettingsModule'),
  'OrganisationSettingsModule',
);
const YourTeamPage = lazyNamed(
  () => import('@/features/onboarding/pages/YourTeamPage'),
  'YourTeamPage',
);
const FirstIntakePage = lazyNamed(
  () => import('@/features/onboarding/pages/FirstIntakePage'),
  'FirstIntakePage',
);
const OnboardingCompletePage = lazyNamed(
  () => import('@/features/onboarding/pages/OnboardingCompletePage'),
  'OnboardingCompletePage',
);
const IncidentManagementHubPage = lazyNamed(
  () => import('@/features/incidents/pages/IncidentManagementHubPage'),
  'IncidentManagementHubPage',
);
const IncidentNewIntakePage = lazyNamed(
  () => import('@/features/incidents/pages/IncidentNewIntakePage'),
  'IncidentNewIntakePage',
);
const RosterLogisticsPage = lazyNamed(
  () => import('@/features/roster/pages/RosterLogisticsPage'),
  'RosterLogisticsPage',
);

function routeElement(node: ReactNode): ReactNode {
  return <Suspense fallback={<RouteLoadingFallback />}>{node}</Suspense>;
}

/** Hub routes driven by a table so adding one is a single-line change. */
const HUB_ROUTES: readonly {
  path: string;
  hubKey: never;
}[] = [];

export const router = createBrowserRouter([
  { path: paths.root, element: <RootRedirect /> },
  { path: paths.login, element: routeElement(<LoginPage />) },
  { path: paths.signup, element: routeElement(<SignUpPage />) },
  { path: paths.verifyEmail, element: routeElement(<VerifyEmailPage />) },
  { path: paths.portalActivate, element: routeElement(<PortalInviteActivatePage />) },
  {
    element: (
      <RequireAuth>
        <OnboardingLayout />
      </RequireAuth>
    ),
    children: [
      {
        path: paths.onboarding,
        element: <OnboardingIndexRedirect />,
      },
      {
        path: paths.onboardingIdentity,
        element: routeElement(<OrganisationIdentityPage />),
      },
      {
        path: paths.onboardingServiceSites,
        element: routeElement(<ServiceSitesPage />),
      },
      {
        path: paths.onboardingYourTeam,
        element: routeElement(<YourTeamPage />),
      },
      {
        path: paths.onboardingFirstIntake,
        element: routeElement(<FirstIntakePage />),
      },
      {
        path: paths.onboardingComplete,
        element: routeElement(<OnboardingCompletePage />),
      },
    ],
  },
  {
    element: (
      <RequireAuth>
        <WorkspaceLayout />
      </RequireAuth>
    ),
    children: [
      { path: paths.dashboard, element: routeElement(<DashboardGatePage />) },
      {
        element: (
          <RequireOnboardingComplete>
            <Outlet />
          </RequireOnboardingComplete>
        ),
        children: [
          {
            path: paths.guardianDashboard,
            element: routeElement(
              <RequireGuardianOnly>
                <GuardianDashboardPage />
              </RequireGuardianOnly>,
            ),
          },
          {
            path: paths.guardianParticipantProfileRoute,
            element: routeElement(
              <RequireGuardianOnly>
                <GuardianParticipantProfilePage />
              </RequireGuardianOnly>,
            ),
          },
          {
            path: paths.participantHub,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <PatientsHubPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.patientsArchived,
            element: routeElement(
              <RequireVerified>
                <ArchivedPatientsPage />
              </RequireVerified>,
            ),
          },
          {
            path: paths.patientsImportReview,
            element: routeElement(
              <RequireVerified>
                <RequireParticipantsManage>
                  <PatientsImportReviewPage />
                </RequireParticipantsManage>
              </RequireVerified>,
            ),
          },
          {
            path: paths.patientsNewIntake,
            element: routeElement(
              <RequireVerified>
                <RequireParticipantsManage>
                  <NewPatientIntakePage />
                </RequireParticipantsManage>
              </RequireVerified>,
            ),
          },
          {
            path: paths.patientProfileRoute,
            element: routeElement(
              <RequireVerified>
                <PatientProfilePage />
              </RequireVerified>,
            ),
          },
          {
            path: paths.patientCarePlanDraftRoute,
            element: routeElement(
              <RequireVerified>
                <RequireParticipantsManage>
                  <PatientCarePlanDraftPage />
                </RequireParticipantsManage>
              </RequireVerified>,
            ),
          },
          {
            path: paths.legacyOrganisationServiceSitesPath,
            element: <Navigate to={paths.organisationSettingsServiceSites} replace />,
          },
          {
            path: `${paths.organisationSettings}/*`,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <RequireOrganisationSettingsAccess>
                    <OrganisationSettingsModule />
                  </RequireOrganisationSettingsAccess>
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.workforce,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <WorkforceManagementPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.workforceNewStaff,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <WorkforceNewStaffPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.workforceNewCareTeam,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <WorkforceNewCareTeamPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.workforceStaffProfileRoute,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <WorkforceStaffProfilePage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.trainingHub,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <TrainingHubPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.trainingHubNew,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <TrainingNewProgramPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.trainingHubEditRoute,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <TrainingNewProgramPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.trainingHubDetailRoute,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <TrainingProgramDetailPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.trainingHubPreviewRoute,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <TrainingProgramPreviewPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.incidentManagement,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <IncidentManagementHubPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.incidentManagementNew,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <IncidentNewIntakePage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.incidentManagementEditRoute,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <IncidentNewIntakePage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          {
            path: paths.rosterLogistics,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <RosterLogisticsPage />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          },
          ...HUB_ROUTES.map(({ path, hubKey }) => ({
            path,
            element: routeElement(
              <RequireVerified>
                <RedirectGuardianToDashboard>
                  <HubPlaceholderPage hubKey={hubKey} />
                </RedirectGuardianToDashboard>
              </RequireVerified>,
            ),
          })),
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to={paths.root} replace /> },
]);
